import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getCustomTargetingKeys,
    getPagedCustomTargetingValues,
    createCustomTargetingKey,
    createCustomTargetingValue,
    deleteCustomTargetingValue
} from '@/services/api/custom-targeting';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import {
    Plus,
    Trash2,
    Key,
    Tag,
    Search,
    Circle,
    AlertTriangle,
    ChevronDown,
    ChevronRight,
    ChevronLeft,
    Copy,
    Check,
    Clock,
    X
} from 'lucide-react';
import type {
    CustomTargetingKey,
    CustomTargetingValue,
    CreateKeyInput,
    CreateValueInput
} from '../types/custom-targeting';
import { diagnoseGamError, TONE_STYLES, type GamErrorDiagnosis } from '../lib/gamErrors';
import { parseApiDate, formatDateTime, formatDateTimeShort } from '../lib/apiDate';
import KeyCapacityCards from '../components/KeyCapacityCards';
import AutoCleanupPanel from '../components/AutoCleanupPanel';

export default function CustomTargeting() {
    const [isCreateKeyDialogOpen, setIsCreateKeyDialogOpen] = useState(false);
    const [isCreateValueDialogOpen, setIsCreateValueDialogOpen] = useState(false);
    const [isDeleteValueDialogOpen, setIsDeleteValueDialogOpen] = useState(false);
    const [selectedKeyForValue, setSelectedKeyForValue] = useState<CustomTargetingKey | null>(null);
    const [selectedValue, setSelectedValue] = useState<CustomTargetingValue | null>(null);
    const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [keyFormData, setKeyFormData] = useState<CreateKeyInput>({
        name: '',
        display_name: ''
    });

    const [valueFormData, setValueFormData] = useState<CreateValueInput>({
        key_id: '',
        name: '',
        display_name: ''
    });

    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Query para buscar keys
    const { data: keys, isLoading: isLoadingKeys } = useQuery({
        queryKey: ['custom-targeting-keys'],
        queryFn: () => getCustomTargetingKeys(),
    });

    // Filtrar e ordenar keys
    const filteredKeys = useMemo(() => {
        if (!keys) return [];
        const filtered = keys.filter(key => {
            const name = key.name?.toLowerCase() || '';
            const displayName = key.display_name?.toLowerCase() || '';
            const search = searchTerm.toLowerCase();
            return name.includes(search) || displayName.includes(search);
        });
        return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [keys, searchTerm]);

    // Verificar se a key selecionada ainda existe nos resultados filtrados
    const selectedKey = useMemo(() => {
        return filteredKeys.find(k => k._id === selectedKeyId) || null;
    }, [filteredKeys, selectedKeyId]);

    // Mutation para criar key
    const createKeyMutation = useMutation({
        mutationFn: createCustomTargetingKey,
        onSuccess: () => {
            toast({
                title: 'Key criada!',
                description: 'A Custom Targeting Key foi criada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['custom-targeting-keys'] });
            setIsCreateKeyDialogOpen(false);
            setKeyFormData({ name: '', display_name: '' });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar key',
                description: error.response?.data?.detail || 'Ocorreu um erro ao criar a key.',
            });
        },
    });

    // Mutation para criar value
    const createValueMutation = useMutation({
        mutationFn: createCustomTargetingValue,
        onSuccess: () => {
            toast({
                title: 'Value criado!',
                description: 'O Custom Targeting Value foi criado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['custom-targeting-values'] });
            setIsCreateValueDialogOpen(false);
            setValueFormData({ key_id: '', name: '', display_name: '' });
            setSelectedKeyForValue(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar value',
                description: error.response?.data?.detail || 'Ocorreu um erro ao criar o value.',
            });
        },
    });

    // Mutation para deletar value
    const deleteValueMutation = useMutation({
        mutationFn: deleteCustomTargetingValue,
        onSuccess: () => {
            toast({
                title: 'Value deletado!',
                description: 'O value foi removido com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['custom-targeting-values'] });
            setIsDeleteValueDialogOpen(false);
            setSelectedValue(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao deletar value',
                description: error.response?.data?.detail || 'Ocorreu um erro ao deletar o value.',
            });
        },
    });

    const handleCreateKeySubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!keyFormData.name.trim() || !keyFormData.display_name.trim()) {
            toast({
                variant: 'destructive',
                title: 'Campos obrigatórios',
                description: 'Por favor, preencha todos os campos.',
            });
            return;
        }

        createKeyMutation.mutate(keyFormData);
    };

    const handleCreateValueSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!valueFormData.name.trim() || !valueFormData.display_name.trim()) {
            toast({
                variant: 'destructive',
                title: 'Campos obrigatórios',
                description: 'Por favor, preencha todos os campos.',
            });
            return;
        }

        createValueMutation.mutate(valueFormData);
    };

    const openCreateValueDialog = (key: CustomTargetingKey) => {
        setSelectedKeyForValue(key);
        setValueFormData({
            key_id: key._id,
            name: '',
            display_name: ''
        });
        setIsCreateValueDialogOpen(true);
    };

    const openDeleteValueDialog = (value: CustomTargetingValue) => {
        setSelectedValue(value);
        setIsDeleteValueDialogOpen(true);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
            case 'processed':
                return 'text-emerald-600';
            case 'pending':
                return 'text-amber-600';
            case 'error':
                return 'text-rose-600';
            default:
                return 'text-slate-600';
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                        Custom Targeting
                    </h1>
                    <p className="text-sm text-slate-600">
                        Gerencie keys e values do Google Ad Manager
                    </p>
                </div>

                <KeyCapacityCards />

                <AutoCleanupPanel />

                {/* Actions Bar */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Buscar keys..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 bg-white border-slate-200"
                        />
                    </div>
                    <Button
                        onClick={() => setIsCreateKeyDialogOpen(true)}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                    >
                        <Plus className="h-4 w-4 mr-2 text-white" />
                        Nova Key
                    </Button>
                </div>

                {/* Stats */}
                {filteredKeys.length > 0 && (
                    <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
                        <div className="text-sm text-slate-600">
                            Total: <span className="font-medium text-slate-900">{keys?.length || 0}</span> keys
                            {searchTerm && (
                                <span className="ml-2">
                                    · Filtradas: <span className="font-medium text-slate-900">{filteredKeys.length}</span>
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {isLoadingKeys && (
                    <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                        <div className="inline-block h-8 w-8 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
                        <p className="mt-4 text-sm text-slate-600">Carregando...</p>
                    </div>
                )}

                {/* Keys Grid */}
                {!isLoadingKeys && filteredKeys.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Keys List */}
                        <div className="lg:col-span-1 space-y-2">
                            {filteredKeys.map((key) => (
                                <button
                                    key={key._id}
                                    onClick={() => setSelectedKeyId(key._id)}
                                    className={`w-full text-left transition-all ${
                                        selectedKeyId === key._id
                                            ? 'bg-white border-slate-900 shadow-sm'
                                            : 'bg-white border-slate-200 hover:border-slate-300'
                                    } rounded-lg border p-4`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Key className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                                <h3 className="text-sm font-medium text-slate-900 truncate">
                                                    {key.name}
                                                </h3>
                                            </div>
                                            <p className="text-xs text-slate-500 truncate">
                                                {key.display_name}
                                            </p>
                                            {key.gam_key_id && (
                                                <p className="text-xs text-slate-400 font-mono mt-1">
                                                    #{key.gam_key_id}
                                                </p>
                                            )}
                                            {key.status === 'error' && (() => {
                                                const d = diagnoseGamError(key.error_message, { name: key.name });
                                                if (!d) return null;
                                                return (
                                                    <div
                                                        className="flex items-center gap-1.5 mt-1.5"
                                                        title={d.raw}
                                                    >
                                                        <AlertTriangle
                                                            className={`h-3 w-3 flex-shrink-0 ${TONE_STYLES[d.tone].icon}`}
                                                        />
                                                        <span className="text-xs text-slate-600 truncate">
                                                            {d.title}
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                            <Circle className={`h-2 w-2 fill-current ${getStatusColor(key.status)}`} />
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Values Panel */}
                        <div className="lg:col-span-2">
                            {selectedKey ? (
                                <ValuesPanel
                                    keyId={selectedKey._id}
                                    keyData={selectedKey}
                                    //@ts-ignore
                                    onCreateValue={openCreateValueDialog}
                                    onDeleteValue={openDeleteValueDialog}
                                    getStatusColor={getStatusColor}
                                />
                            ) : (
                                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                                    <Tag className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                                    <p className="text-sm text-slate-600">
                                        Selecione uma key para ver seus values
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {!isLoadingKeys && filteredKeys.length === 0 && (
                    <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Key className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-sm font-medium text-slate-900 mb-1">
                            {searchTerm ? 'Nenhuma key encontrada' : 'Nenhuma key cadastrada'}
                        </h3>
                        <p className="text-sm text-slate-500 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece criando sua primeira Custom Targeting Key'
                            }
                        </p>
                        {!searchTerm && (
                            <Button
                                onClick={() => setIsCreateKeyDialogOpen(true)}
                                className="bg-slate-900 hover:bg-slate-800 text-white"
                            >
                                <Plus className="h-4 w-4 mr-2 text-white" />
                                Criar Key
                            </Button>
                        )}
                    </div>
                )}

                {/* Create Key Dialog */}
                <Dialog open={isCreateKeyDialogOpen} onOpenChange={setIsCreateKeyDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-lg">Nova Custom Targeting Key</DialogTitle>
                            <DialogDescription className="text-sm">
                                Crie uma nova key no Google Ad Manager
                            </DialogDescription>
                        </DialogHeader>

                        <form onSubmit={handleCreateKeySubmit} className="space-y-4 mt-2">
                            <div className="space-y-2">
                                <Label htmlFor="key-name" className="text-sm font-medium">
                                    Nome técnico
                                </Label>
                                <Input
                                    id="key-name"
                                    placeholder="utm_campaign"
                                    value={keyFormData.name}
                                    onChange={(e) =>
                                        setKeyFormData({ ...keyFormData, name: e.target.value })
                                    }
                                    disabled={createKeyMutation.isPending}
                                    className="border-slate-200"
                                />
                                <p className="text-xs text-slate-500">
                                    Nome da key (snake_case recomendado)
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="key-display-name" className="text-sm font-medium">
                                    Nome de exibição
                                </Label>
                                <Input
                                    id="key-display-name"
                                    placeholder="UTM Campaign"
                                    value={keyFormData.display_name}
                                    onChange={(e) =>
                                        setKeyFormData({ ...keyFormData, display_name: e.target.value })
                                    }
                                    disabled={createKeyMutation.isPending}
                                    className="border-slate-200"
                                />
                                <p className="text-xs text-slate-500">
                                    Nome amigável para exibição
                                </p>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => setIsCreateKeyDialogOpen(false)}
                                    disabled={createKeyMutation.isPending}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                                    disabled={createKeyMutation.isPending}
                                >
                                    {createKeyMutation.isPending ? 'Criando...' : 'Criar'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Create Value Dialog */}
                <Dialog open={isCreateValueDialogOpen} onOpenChange={setIsCreateValueDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-lg">Novo Value</DialogTitle>
                            <DialogDescription className="text-sm">
                                Key: <span className="font-medium text-slate-900">{selectedKeyForValue?.name}</span>
                            </DialogDescription>
                        </DialogHeader>

                        <form onSubmit={handleCreateValueSubmit} className="space-y-4 mt-2">
                            <div className="space-y-2">
                                <Label htmlFor="value-name" className="text-sm font-medium">
                                    Nome técnico
                                </Label>
                                <Input
                                    id="value-name"
                                    placeholder="teste123"
                                    value={valueFormData.name}
                                    onChange={(e) =>
                                        setValueFormData({ ...valueFormData, name: e.target.value })
                                    }
                                    disabled={createValueMutation.isPending}
                                    className="border-slate-200"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="value-display-name" className="text-sm font-medium">
                                    Nome de exibição
                                </Label>
                                <Input
                                    id="value-display-name"
                                    placeholder="Teste 123"
                                    value={valueFormData.display_name}
                                    onChange={(e) =>
                                        setValueFormData({ ...valueFormData, display_name: e.target.value })
                                    }
                                    disabled={createValueMutation.isPending}
                                    className="border-slate-200"
                                />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => {
                                        setIsCreateValueDialogOpen(false);
                                        setSelectedKeyForValue(null);
                                    }}
                                    disabled={createValueMutation.isPending}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                                    disabled={createValueMutation.isPending}
                                >
                                    {createValueMutation.isPending ? 'Criando...' : 'Criar'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Delete Value Dialog */}
                <Dialog open={isDeleteValueDialogOpen} onOpenChange={setIsDeleteValueDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-lg">Deletar Value</DialogTitle>
                            <DialogDescription className="text-sm">
                                Confirma a exclusão de <span className="font-medium text-slate-900">{selectedValue?.name}</span>?
                                Esta ação remove apenas do banco de dados local.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="flex gap-3 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => setIsDeleteValueDialogOpen(false)}
                                disabled={deleteValueMutation.isPending}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="destructive"
                                className="flex-1"
                                onClick={() => selectedValue && deleteValueMutation.mutate(selectedValue._id)}
                                disabled={deleteValueMutation.isPending}
                            >
                                {deleteValueMutation.isPending ? 'Deletando...' : 'Deletar'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}

// Componente do painel de values
function ValuesPanel({
                         keyId,
                         keyData,
                         onCreateValue,
                         onDeleteValue,
                         getStatusColor
                     }: {
    keyId: string;
    keyData: CustomTargetingKey;
    onCreateValue: () => void;
    onDeleteValue: (value: CustomTargetingValue) => void;
    getStatusColor: (status: string) => string;
}) {
    const [statusFilter, setStatusFilter] = useState<'all' | 'error' | 'pending' | 'processed'>('all');
    const [periodFilter, setPeriodFilter] = useState<'all' | '24h' | '7d' | '30d' | 'custom'>('all');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [errorCodeFilter, setErrorCodeFilter] = useState('all');
    const [page, setPage] = useState(1);

    // 100 por página: o suficiente para varrer com o olho sem tornar a
    // requisição pesada. Uma key chega a 800 mil values.
    const POR_PAGINA = 100;

    // O período vira parâmetro de query. Os atalhos mandam o instante exato em
    // ISO; o período específico manda só a data, e o servidor a trata como o
    // dia inteiro.
    const periodo = useMemo(() => {
        const DIA = 24 * 60 * 60 * 1000;
        const agora = Date.now();

        if (periodFilter === '24h') return { dateFrom: new Date(agora - DIA).toISOString() };
        if (periodFilter === '7d') return { dateFrom: new Date(agora - 7 * DIA).toISOString() };
        if (periodFilter === '30d') return { dateFrom: new Date(agora - 30 * DIA).toISOString() };
        if (periodFilter === 'custom') {
            return {
                dateFrom: customFrom || undefined,
                dateTo: customTo || undefined,
            };
        }
        return {};
    }, [periodFilter, customFrom, customTo]);

    // Trocar de filtro com a página 7 aberta traria uma página vazia.
    useEffect(() => {
        setPage(1);
    }, [keyId, statusFilter, periodFilter, customFrom, customTo]);

    const { data, isLoading, isFetching } = useQuery({
        queryKey: [
            'custom-targeting-values', keyId, statusFilter, periodFilter, customFrom, customTo, page,
        ],
        queryFn: () => getPagedCustomTargetingValues({
            keyId,
            status: statusFilter === 'all' ? undefined : statusFilter,
            dateFrom: periodo.dateFrom,
            dateTo: periodo.dateTo,
            page,
            perPage: POR_PAGINA,
        }),
        // Mantém a página anterior visível enquanto a próxima carrega, para a
        // lista não sumir a cada clique em "Próxima".
        placeholderData: (anterior) => anterior,
    });

    const values = data?.data ?? [];

    // Contagens vêm do servidor: com uma página de cada vez, não dá para
    // contar em memória. São os números dos botões de status.
    const counts = data?.counts ?? { all: 0, error: 0, pending: 0, processed: 0 };

    // Diagnóstico e data da tentativa são calculados uma vez por value e
    // reaproveitados pelo filtro de tipo de erro e pela renderização da linha.
    const enriched = useMemo(
        () =>
            values.map((value) => ({
                value,
                diagnosis:
                    value.status === 'error'
                        ? diagnoseGamError(value.error_message, { name: value.name })
                        : null,
                attemptedAt: parseApiDate(value.updated_at),
            })),
        [values]
    );

    // Tipos de erro presentes NESTA PÁGINA. O diagnóstico é feito no navegador,
    // a partir da mensagem do GAM, então o servidor não sabe filtrar por ele —
    // por isso o seletor diz "nesta página" e não promete o total da key.
    const errorCodes = useMemo(() => {
        const map = new Map<string, { code: string; title: string; count: number }>();
        enriched.forEach(({ diagnosis }) => {
            if (!diagnosis) return;
            const entry = map.get(diagnosis.code);
            if (entry) entry.count += 1;
            else map.set(diagnosis.code, { code: diagnosis.code, title: diagnosis.title, count: 1 });
        });
        return [...map.values()].sort((a, b) => b.count - a.count);
    }, [enriched]);

    const visibleValues = useMemo(
        () =>
            enriched.filter(({ diagnosis }) =>
                errorCodeFilter === 'all' || diagnosis?.code === errorCodeFilter
            ),
        [enriched, errorCodeFilter]
    );

    const isFiltered = statusFilter !== 'all' || periodFilter !== 'all' || errorCodeFilter !== 'all';

    const clearFilters = () => {
        setStatusFilter('all');
        setPeriodFilter('all');
        setErrorCodeFilter('all');
        setCustomFrom('');
        setCustomTo('');
    };

    const total = data?.total ?? 0;
    const totalPaginas = data?.pages ?? 0;
    const primeiroDaPagina = total === 0 ? 0 : (page - 1) * POR_PAGINA + 1;
    const ultimoDaPagina = Math.min(page * POR_PAGINA, total);

    const FILTERS: { id: typeof statusFilter; label: string }[] = [
        { id: 'all', label: 'Todos' },
        { id: 'error', label: 'Com erro' },
        { id: 'pending', label: 'Pendentes' },
        { id: 'processed', label: 'No GAM' },
    ];

    const PERIODS: { id: typeof periodFilter; label: string }[] = [
        { id: 'all', label: 'Qualquer data' },
        { id: '24h', label: 'Últimas 24h' },
        { id: '7d', label: 'Últimos 7 dias' },
        { id: '30d', label: 'Últimos 30 dias' },
        { id: 'custom', label: 'Período específico' },
    ];

    return (
        <div className="bg-white rounded-lg border border-slate-200">
            {/* Header */}
            <div className="border-b border-slate-200 p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-medium text-slate-900">{keyData.display_name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{keyData.name}</p>
                    </div>
                    <Button
                        size="sm"
                        onClick={onCreateValue}
                        className="bg-slate-900 hover:bg-slate-800 text-white h-8"
                    >
                        <Plus className="h-3.5 w-3.5 mr-1.5 text-white" />
                        Value
                    </Button>
                </div>

                {/* Filtro por status — atalho para isolar as falhas */}
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                    {FILTERS.map((f) => {
                        const active = statusFilter === f.id;
                        const isError = f.id === 'error' && counts.error > 0;
                        return (
                            <button
                                key={f.id}
                                onClick={() => setStatusFilter(f.id)}
                                className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                                    active
                                        ? 'bg-slate-900 text-white border-slate-900'
                                        : isError
                                            ? 'bg-rose-50 text-rose-700 border-rose-200 hover:border-rose-300'
                                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                                }`}
                            >
                                {f.label}
                                <span className={`ml-1.5 ${active ? 'text-slate-300' : 'text-slate-400'}`}>
                                    {counts[f.id].toLocaleString('pt-BR')}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Filtros de data da tentativa e de tipo de erro */}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-slate-400" />
                        <select
                            value={periodFilter}
                            onChange={(e) => setPeriodFilter(e.target.value as typeof periodFilter)}
                            className="text-xs bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-700 hover:border-slate-300 focus:outline-none focus:border-slate-400"
                        >
                            {PERIODS.map((p) => (
                                <option key={p.id} value={p.id}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    {periodFilter === 'custom' && (
                        <div className="flex items-center gap-1.5">
                            <input
                                type="date"
                                value={customFrom}
                                onChange={(e) => setCustomFrom(e.target.value)}
                                className="text-xs bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-700 focus:outline-none focus:border-slate-400"
                            />
                            <span className="text-xs text-slate-400">até</span>
                            <input
                                type="date"
                                value={customTo}
                                onChange={(e) => setCustomTo(e.target.value)}
                                className="text-xs bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-700 focus:outline-none focus:border-slate-400"
                            />
                        </div>
                    )}

                    {errorCodes.length > 0 && (
                        <div className="flex items-center gap-1.5">
                            <AlertTriangle className="h-3.5 w-3.5 text-slate-400" />
                            <select
                                value={errorCodeFilter}
                                onChange={(e) => setErrorCodeFilter(e.target.value)}
                                className="text-xs bg-white border border-slate-200 rounded-md px-2 py-1 text-slate-700 hover:border-slate-300 focus:outline-none focus:border-slate-400 max-w-[280px]"
                                title="O tipo de erro é identificado no navegador, então este filtro vale para a página aberta"
                            >
                                <option value="all">Todos os tipos de erro</option>
                                {errorCodes.map((e) => (
                                    <option key={e.code} value={e.code}>
                                        {e.title} ({e.count} nesta página)
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {isFiltered && (
                        <button
                            onClick={clearFilters}
                            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 px-1.5 py-1"
                        >
                            <X className="h-3 w-3" />
                            Limpar
                        </button>
                    )}

                    <span className="text-xs text-slate-500 ml-auto">
                        {total > 0
                            ? `${primeiroDaPagina.toLocaleString('pt-BR')}–${ultimoDaPagina.toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')}`
                            : '0 values'}
                        {isFetching && !isLoading && <span className="ml-2 text-slate-400">atualizando...</span>}
                    </span>
                </div>
            </div>

            {/* Content */}
            <div className="p-4">
                {isLoading ? (
                    <div className="text-center py-8">
                        <div className="inline-block h-6 w-6 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
                    </div>
                ) : total === 0 ? (
                    <div className="text-center py-8">
                        <Tag className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">
                            {isFiltered ? 'Nenhum value com os filtros aplicados' : 'Nenhum value cadastrado'}
                        </p>
                        {isFiltered && (
                            <button
                                onClick={clearFilters}
                                className="text-xs text-slate-500 hover:text-slate-900 underline mt-2"
                            >
                                Limpar filtros
                            </button>
                        )}
                    </div>
                ) : visibleValues.length === 0 ? (
                    <div className="text-center py-8">
                        <Tag className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">
                            Nenhum value deste tipo de erro nesta página
                        </p>
                        <button
                            onClick={() => setErrorCodeFilter('all')}
                            className="text-xs text-slate-500 hover:text-slate-900 underline mt-2"
                        >
                            Mostrar todos os tipos
                        </button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {visibleValues.map(({ value, diagnosis, attemptedAt }) => (
                            <ValueRow
                                key={value._id}
                                value={value}
                                diagnosis={diagnosis}
                                attemptedAt={attemptedAt}
                                onDelete={onDeleteValue}
                                getStatusColor={getStatusColor}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Paginação */}
            {totalPaginas > 1 && (
                <div className="border-t border-slate-200 p-3 flex items-center justify-between">
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1 || isFetching}
                    >
                        <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                        Anterior
                    </Button>

                    <span className="text-xs text-slate-500">
                        Página {page.toLocaleString('pt-BR')} de {totalPaginas.toLocaleString('pt-BR')}
                    </span>

                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
                        disabled={page >= totalPaginas || isFetching}
                    >
                        Próxima
                        <ChevronRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                </div>
            )}
        </div>
    );
}

// Linha de um value. Quando está em erro, permite abrir o diagnóstico da falha.
function ValueRow({
                      value,
                      diagnosis,
                      attemptedAt,
                      onDelete,
                      getStatusColor
                  }: {
    value: CustomTargetingValue;
    diagnosis: GamErrorDiagnosis | null;
    attemptedAt: Date | null;
    onDelete: (value: CustomTargetingValue) => void;
    getStatusColor: (status: string) => string;
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [copied, setCopied] = useState(false);

    const tone = diagnosis ? TONE_STYLES[diagnosis.tone] : null;

    const copyRaw = async () => {
        if (!diagnosis) return;
        try {
            await navigator.clipboard.writeText(diagnosis.raw);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard bloqueado pelo navegador: a mensagem segue visível para seleção manual.
        }
    };

    return (
        <div
            className={`rounded-lg border transition-colors ${
                diagnosis
                    ? 'border-rose-200 hover:border-rose-300'
                    : 'border-slate-200 hover:border-slate-300'
            }`}
        >
            <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Tag className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">
                            {value.name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                            {value.display_name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                            {value.gam_value_id && (
                                <span className="text-xs text-slate-400 font-mono">
                                    #{value.gam_value_id}
                                </span>
                            )}
                            {attemptedAt && (
                                <span
                                    className="flex items-center gap-1 text-xs text-slate-400"
                                    title={`${diagnosis ? 'Última tentativa' : 'Atualizado'}: ${formatDateTime(attemptedAt)}`}
                                >
                                    <Clock className="h-3 w-3" />
                                    {formatDateTimeShort(attemptedAt)}
                                </span>
                            )}
                        </div>

                        {/* Resumo do erro: visível sem precisar abrir nada */}
                        {diagnosis && (
                            <button
                                onClick={() => setIsExpanded((v) => !v)}
                                className="flex items-center gap-1.5 mt-1.5 text-left group"
                            >
                                {isExpanded
                                    ? <ChevronDown className="h-3 w-3 text-slate-400 flex-shrink-0" />
                                    : <ChevronRight className="h-3 w-3 text-slate-400 flex-shrink-0" />}
                                <span
                                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${tone!.badge}`}
                                >
                                    {diagnosis.code}
                                </span>
                                <span className="text-xs text-slate-600 group-hover:text-slate-900 truncate">
                                    {diagnosis.title}
                                </span>
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Circle
                        className={`h-2 w-2 fill-current ${getStatusColor(value.status)}`}
                        aria-label={value.status}
                    />
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(value)}
                        className="h-8 w-8 p-0 hover:bg-rose-50 hover:text-rose-600"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>

            {/* Diagnóstico completo */}
            {diagnosis && isExpanded && (
                <div className={`mx-3 mb-3 rounded-md border p-3 ${tone!.panel}`}>
                    <div className="flex items-start gap-2">
                        <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${tone!.icon}`} />
                        <div className="min-w-0 flex-1 space-y-2">
                            <div>
                                <p className="text-xs font-medium text-slate-900">O que aconteceu</p>
                                <p className="text-xs text-slate-700 mt-0.5">{diagnosis.detail}</p>
                            </div>

                            <div>
                                <p className="text-xs font-medium text-slate-900">Como resolver</p>
                                <p className="text-xs text-slate-700 mt-0.5">{diagnosis.hint}</p>
                            </div>

                            {diagnosis.retryable && (
                                <p className="text-xs text-slate-600 italic">
                                    Falha recuperável — este value tende a passar numa nova tentativa.
                                </p>
                            )}

                            <div>
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-xs font-medium text-slate-900">Resposta do GAM</p>
                                    <button
                                        onClick={copyRaw}
                                        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
                                    >
                                        {copied
                                            ? <><Check className="h-3 w-3" /> Copiado</>
                                            : <><Copy className="h-3 w-3" /> Copiar</>}
                                    </button>
                                </div>
                                <pre className="mt-1 text-[11px] font-mono text-slate-600 bg-white/70 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                                    {diagnosis.raw}
                                </pre>
                            </div>

                            {attemptedAt && (
                                <p className="text-[11px] text-slate-500">
                                    Última tentativa: {formatDateTime(attemptedAt)}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
